import * as React from "react";
import { X, CheckCircle2, AlertCircle, Info } from "lucide-react";
import { cn } from "@/lib/utils";

export interface ToastProps {
  id: string;
  title?: string;
  description: string;
  variant?: "default" | "destructive" | "success";
  duration?: number;
  onClose: () => void;
}

const variantConfig = {
  default: {
    icon: Info,
    accent: "bg-primary",
    container: "border border-slate-200 bg-white text-slate-900 shadow-lg",
    iconBg: "bg-primary/10",
    iconColor: "text-primary",
    progress: "bg-primary/30",
  },
  success: {
    icon: CheckCircle2,
    accent: "bg-green-500",
    container: "border border-green-200 bg-green-50 text-green-900 shadow-lg",
    iconBg: "bg-green-100",
    iconColor: "text-green-600",
    progress: "bg-green-500/40",
  },
  destructive: {
    icon: AlertCircle,
    accent: "bg-red-500",
    container: "border border-red-200 bg-red-50 text-red-900 shadow-lg",
    iconBg: "bg-red-100",
    iconColor: "text-red-600",
    progress: "bg-red-500/40",
  },
};

export function Toast({
  id: _id,
  title,
  description,
  variant = "default",
  duration = 5000,
  onClose,
}: ToastProps) {
  const config = variantConfig[variant];
  const Icon = config.icon;

  return (
    <div
      className={cn(
        "animate-toast-slide-in pointer-events-auto relative flex w-full overflow-hidden rounded-lg",
        config.container
      )}
      role="alert"
    >
      {/* Left accent bar */}
      <div className={cn("absolute left-0 top-0 bottom-0 w-1 shrink-0", config.accent)} />

      <div className="flex flex-1 items-start gap-3 pl-4 pr-10 py-4">
        {/* Icon */}
        <div
          className={cn(
            "flex h-9 w-9 shrink-0 items-center justify-center rounded-lg",
            config.iconBg,
            config.iconColor
          )}
        >
          <Icon className="h-5 w-5" strokeWidth={2} />
        </div>

        {/* Content */}
        <div className="min-w-0 flex-1 pt-0.5">
          {title ? <p className="font-semibold text-sm leading-tight">{title}</p> : null}
          <p className={cn("text-sm leading-relaxed", title ? "mt-1 opacity-80" : "")}>
            {description}
          </p>
        </div>
      </div>

      {/* Close button */}
      <button
        type="button"
        onClick={onClose}
        className="absolute right-2 top-2 rounded-md p-1.5 opacity-50 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-1"
        aria-label="Dismiss notification"
      >
        <X className="h-4 w-4" />
      </button>

      {/* Progress bar - shrinks over time to show auto-dismiss countdown */}
      {duration > 0 && (
        <div
          className={cn(
            "absolute bottom-0 left-0 right-0 h-0.5 origin-left animate-toast-progress",
            config.progress
          )}
          style={{ "--toast-duration": `${duration}ms` } as React.CSSProperties}
        />
      )}
    </div>
  );
}

export function ToastContainer({
  toasts,
  onClose,
}: {
  toasts: ToastProps[];
  onClose: (id: string) => void;
}) {
  if (toasts.length === 0) return null;

  return (
    <div
      className="fixed top-4 right-4 z-[100] flex max-h-[calc(100vh-2rem)] w-full flex-col gap-3 p-4 sm:max-w-[400px]"
      aria-live="polite"
      aria-label="Notifications"
    >
      {toasts.map((toast) => (
        <Toast key={toast.id} {...toast} onClose={() => onClose(toast.id)} />
      ))}
    </div>
  );
}
